// src/components/Layout/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiUsers,
  FiDollarSign,
  FiCalendar,
  FiSettings,
  FiBriefcase,
  FiPieChart,
  FiPhoneCall,
  FiTrendingUp,
  FiUserCheck,
  FiLogOut,
  FiX,
  FiClock,
  FiMessageSquare,
  FiBook,
  FiChevronDown,
  FiChevronRight,
  FiHelpCircle,
  FiFileText,
  FiList,
  FiPlusCircle,
} from 'react-icons/fi';
import { getCurrentUser, logout } from '../../services/authService';
import websheddlogo from '../../assets/websheddlogo.svg';

const Sidebar = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUser();
  const isAdminOrHR = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';
  const isEmployee = currentUser?.role === 'employee';

  const studentFormSubmenuItems = [
    {
      path: '/student-form',
      name: ' student Form',
      icon: FiFileText,
    },
    {
      path: '/queries',
      name: ' Queries Form',
      icon: FiList,
    },
  ];

  // HR/Admin Student Form submenu items with Queries inside
  const hrStudentFormSubmenuItems = [
    {
      path: '/hr-student-forms',
      name: 'student Forms',
      icon: FiBook,
    },
    // Add Queries as a sub-item inside Student Form for HR
    {
      path: '/queries',
      name: 'All Queries Form ',
      icon: FiList,
    },


  ];

  // Select the correct submenu items based on role
  const studentFormItems = isAdminOrHR ? hrStudentFormSubmenuItems : studentFormSubmenuItems;

  // Check if any student form submenu item is active
  const isStudentFormActive = studentFormItems.some(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  // State for expanded submenus - initialize based on active state
  const [expandedMenus, setExpandedMenus] = useState({
    studentForms: isStudentFormActive,
  });

  // Update expanded state when location changes
  useEffect(() => {
    const isActive = studentFormItems.some(
      item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    );
    setExpandedMenus(prev => ({
      ...prev,
      studentForms: isActive ? true : prev.studentForms,
    }));
  }, [location.pathname]);

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  // Employee only navigation
  const employeeNavItems = [
    { path: '/dashboard', name: 'Dashboard', icon: FiHome },

    { path: '/employee-dashboard', name: 'Daily Target', icon: FiUserCheck },
    { path: '/attendance', name: 'Attendance', icon: FiCalendar },
    { path: '/performance', name: 'Performance', icon: FiTrendingUp },
    { path: '/leave', name: 'My Leaves', icon: FiCalendar },
  ];

  // Admin/HR only navigation
  const adminNavItems = [
    { path: '/dashboard', name: 'Dashboard', icon: FiHome },
    { path: '/employees', name: 'Employees', icon: FiUsers },
    { path: '/attendance', name: 'Attendance', icon: FiCalendar },
    { path: '/performance', name: 'Performance', icon: FiTrendingUp },
    { path: '/daily-updates', name: 'Daily Updates', icon: FiMessageSquare },
    { path: '/leave', name: 'Leave', icon: FiDollarSign },
  ];

  // Select navigation based on role
  const baseNavItems = isAdminOrHR ? adminNavItems : employeeNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (onClose) onClose();
  };

  return (
    <aside className="h-full flex flex-col bg-white">
      {/* Header with Logo and Close Button */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 min-w-0">
        <div className="flex items-center gap-" style={{ minWidth: 0 }}>
          <img
            src={websheddlogo}
            alt="H"
            style={{
              width: '110px',
              height: '110px',
              minWidth: '110px',
              minHeight: '110px',
              borderRadius: '100%',
              objectFit: 'cover',
              border: '1px solid #e5e7eb',
              display: 'block',
            }}
          />
          <div className="min-w-5 ">
            <h1 className="text-xl font-bold text-gray-800 truncate">SBSHES</h1>
            <p className="text-xs text-gray-500 -mt-0.2 truncate">Dashboard</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 transition md:hidden flex-shrink-0 ml-2"
        >
          <FiX size={20} className="text-gray-600" />
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {baseNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 transition-all duration-200 hover:bg-indigo-50 hover:text-indigo-700 mb-1 ${isActive ? 'active bg-indigo-600 text-white shadow-md hover:bg-indigo-700 hover:text-white' : ''
              }`
            }
          >
            <item.icon className="text-lg" />
            <span className="font-medium text-sm">{item.name}</span>
          </NavLink>
        ))}

        {/* Student Form - Collapsible Submenu (Only show for employees and HR) */}
        {(isEmployee || isAdminOrHR) && (
          <div className="mb-1">
            <button
              onClick={() => toggleMenu('studentForms')}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-gray-700 transition-all duration-200 hover:bg-indigo-50 hover:text-indigo-700 ${isStudentFormActive ? 'bg-indigo-50 text-indigo-700' : ''
                }`}
            >
              <div className="flex items-center gap-3">
                <FiBook className="text-lg" />
                <span className="font-medium text-sm"> Form</span>
              </div>
              {expandedMenus.studentForms ? (
                <FiChevronDown size={16} />
              ) : (
                <FiChevronRight size={16} />
              )}
            </button>

            {/* Submenu Items - Student Form with Queries inside */}
            {expandedMenus.studentForms && (
              <div className="ml-6 mt-1 space-y-1 border-l-2 border-indigo-200 pl-3">
                {studentFormItems.map((subItem) => {
                  const Icon = subItem.icon;
                  const isQueryItem = subItem.path === '/queries' || subItem.path === '/queries/groups';

                  return (
                    <NavLink
                      key={subItem.path}
                      to={subItem.path}
                      onClick={() => {
                        if (onClose) onClose();
                      }}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-indigo-50 hover:text-indigo-700 ${isActive ? 'text-indigo-700 font-medium bg-indigo-50' : 'text-gray-600'
                        } ${isQueryItem ? 'border-l-2 border-indigo-300' : ''
                        }`
                      }
                    >
                      <Icon className={`text-sm ${isQueryItem ? 'text-indigo-500' : 'text-gray-400'}`} />
                      {subItem.name}
                      {isQueryItem && (
                        <span className="ml-auto text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">
                          Query
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-gray-100 pt-2 pb-4 px-3">


        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="bg-indigo-50 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <FiPieChart className="text-indigo-600 text-sm" />
              <span className="text-xs text-gray-600">SBSHE (BETA) v2.0</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">© 2025 Company Inc.</p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default React.memo(Sidebar);